/**
 * Mathematical utility functions for continuous scoring
 * Replaces magic number if-else chains with smooth mathematical functions
 */
/**
 * Sigmoid function - Maps any value to [0, 1] with smooth S-curve
 * @param x - Input value
 * @param midpoint - Value that maps to 0.5
 * @param steepness - How steep the curve is (higher = steeper)
 */
export function sigmoid(x, midpoint = 0, steepness = 1) {
    return 1 / (1 + Math.exp(-steepness * (x - midpoint)));
}
/**
 * Inverse sigmoid - High values map to low scores
 */
export function inverseSigmoid(x, midpoint = 0, steepness = 1) {
    return 1 - sigmoid(x, midpoint, steepness);
}
/**
 * Gaussian (bell curve) - Peak at ideal value, falls off on both sides
 * @param x - Input value
 * @param ideal - Optimal value (peak of curve)
 * @param width - How wide the acceptable range is
 */
export function gaussian(x, ideal = 0, width = 1) {
    const exponent = -Math.pow(x - ideal, 2) / (2 * width * width);
    return Math.exp(exponent);
}
/**
 * Exponential decay - High values get penalized exponentially
 */
export function exponentialDecay(x, decayRate = 0.1) {
    return Math.exp(-decayRate * x);
}
/**
 * Normalize value to [0, 1] range
 */
export function normalize(value, min = 0, max = 1) {
    if (max === min)
        return 0.5;
    return Math.max(0, Math.min(1, (value - min) / (max - min)));
}
/**
 * Clamp value to [0, 1]
 */
export function clamp01(value) {
    return Math.max(0, Math.min(1, value));
}
/**
 * Calculate statistical metrics for an array of numbers
 * @param values - Array of numerical values
 * @returns Statistical metrics (mean, variance, stdDev, cv)
 */
export function calculateStatistics(values) {
    if (values.length === 0) {
        return { mean: 0, variance: 0, stdDev: 0, cv: 0 };
    }
    const mean = values.reduce((a, b) => a + b, 0) / values.length;
    const variance = values.reduce((sum, v) => sum + (v - mean) ** 2, 0) / values.length;
    const stdDev = Math.sqrt(variance);
    const cv = mean > 0 ? stdDev / mean : 0;
    return { mean, variance, stdDev, cv };
}
/**
 * Analyze timing intervals for bot-like patterns
 * Returns raw analysis data without scoring - caller decides heuristics
 *
 * @param intervals - Array of time intervals (ms)
 * @returns Analysis of interval patterns including stats and uniqueness
 */
export function analyzeIntervals(intervals) {
    if (intervals.length < 2)
        return undefined;
    // Check for identical intervals (round to 10ms to account for floating point)
    const uniqueCount = new Set(intervals.map(i => Math.round(i / 10))).size;
    const allIdentical = uniqueCount === 1;
    // Calculate statistics
    const statistics = calculateStatistics(intervals);
    return {
        statistics,
        uniqueCount,
        allIdentical,
    };
}
/**
 * Score coefficient of variation
 * Maps CV to human-likeness score using Gaussian
 * Ideal CV is around 0.4-0.5 (moderate variation)
 */
export function scoreCoefficientOfVariation(cv) {
    // Too low (<0.1) = bot-like (too consistent)
    // Too high (>1.5) = random noise
    // Ideal = 0.4-0.6 (natural human variation)
    if (cv < 0.05)
        return 0.1; // Almost zero variation - definitely bot
    if (cv > 2.0)
        return 0.3; // Too random
    // Gaussian centered at 0.45 with width of 0.35
    return gaussian(cv, 0.45, 0.35);
}
/**
 * Score entropy - Higher is better (more random = more human)
 * Uses sigmoid to smoothly map entropy to score
 */
export function scoreEntropy(normalizedEntropy) {
    // Entropy of 0.7+ is very human
    // Entropy < 0.3 is very bot-like
    return sigmoid(normalizedEntropy, 0.5, 8); // Steep curve at midpoint 0.5
}
/**
 * Score autocorrelation - Lower is better (less periodic = more human)
 */
export function scoreAutocorrelation(autocorr) {
    // Low autocorrelation (<0.2) is natural
    // High autocorrelation (>0.6) is periodic/bot-like
    return inverseSigmoid(autocorr, 0.4, 10);
}
/**
 * Score jerk - Lower is better (smoother = more human)
 */
export function scoreJerk(avgJerk) {
    // Human jerk typically < 0.01
    // Bot jerk often > 0.1
    // Use inverse sigmoid
    return inverseSigmoid(avgJerk, 0.05, 50);
}
